import { RetryDataOutput, RetryStep } from '@/types/automationSteps.ts';
import { Group, Stack } from '@mantine/core';
import * as React from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import SingleChildDataInputSelector from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleChildDataInputSelector.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT, STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';

type Props = {
  dataOutput: RetryDataOutput;
  onChangeNoDelay: (updatedOutput: RetryDataOutput) => void;
  onChangeDelay: (updatedOutput: RetryDataOutput) => void;
  onDelete: () => void;
  step: RetryStep;
  outputIndex: number;
};

const RetryDataOutputFields = ({
  dataOutput,
  onChangeNoDelay,
  onChangeDelay,
  onDelete,
  step,
  outputIndex,
}: Props) => {
  const invalidChildOutput = useStepSpecificError(
    step,
    StepErrorType.InvalidChildOutput,
    outputIndex,
  );

  const isNewStep = useIsNewStep(step.step_id);

  return (
    <Stack
      style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
      bg={'#F6F6F6'}
      p={'xs'}
      gap={'xs'}
    >
      <Stack>
        <Group gap={'xs'} wrap={'nowrap'} align={'start'}>
          <StyledTextInput
            label="Name"
            value={dataOutput.name}
            placeholder="Output Name"
            onChange={(event) => {
              const value = event.currentTarget.value.replace(/[/\n]/g, '');
              onChangeDelay({ ...dataOutput, name: value });
            }}
            required={true}
            maxLength={STEP_FIELD_MAX_LENGTH}
            w={'100%'}
            error={!dataOutput.name && !isNewStep ? 'Name cannot be empty' : undefined}
          />
          <OutputPinButton step={step} outputId={dataOutput.output_id} />
          <StepFieldsRemoveButton onDelete={onDelete} />
        </Group>
      </Stack>

      <SingleChildDataInputSelector
        step={step}
        label="Child Output"
        dataInput={dataOutput.child_data_output}
        onChange={(dataInput) =>
          onChangeNoDelay({
            ...dataOutput,
            child_data_output: dataInput || DEFAULT_DYNAMIC_DATA_INPUT,
          })
        }
        dataInputError={invalidChildOutput}
        required={true}
        allowedStepIds={step.children.map((child) => child.step_id)}
      />
    </Stack>
  );
};

export default RetryDataOutputFields;
